
import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Snackbar,
  Checkbox,
  Alert,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ayujalogo from "../Logos/AuthLogo.png";
import CustomDialog from "../../CustomDialog";
import { ROLE_OPTIONS } from "../../Configurations/registrationroles.config";

const API_BASE = process.env.REACT_APP_API_URL;

export const fetchUserFromRedis = async () => {
  const userId = sessionStorage.getItem("userId");
  const token = sessionStorage.getItem("token");

  if (!userId || !token) {
    console.log("No userId or token found in session");
    return null;
  }

  try {
    const res = await axios.get(`${API_BASE}/user/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log("✅ Response from Redis user:", res.data);
    const userData = res.data.user;
    if (userData.role === "deliveryagent") {
      if (userData.agent_role) {
        console.log(`✅ Agent role found: ${userData.agent_role}`);
      } else {
        console.warn("⚠️ User is delivery agent but agent_role missing");
      }
    }
    return userData;
  } catch (error) {
    console.error("❌ Error fetching user from Redis:", error);
    return null;
  }
};

const Register = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));       // < 600px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–900px
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));  // < 900px

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [errors, setErrors] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    title: "",
    message: "",
    type: "info",
    buttonText: "OK",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    role: "admin",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const signupAPI = process.env.REACT_APP_REGISTER_URL;
  const roleOptions = ROLE_OPTIONS;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (name === "name") {
      const trimmed = value.trim();
      setErrors((prev) => ({
        ...prev,
        name:
          trimmed.length === 0
            ? ""
            : trimmed.length < 3
            ? "Name must be at least 3 characters long"
            : "",
      }));
    }
  };

  useEffect(() => {
    const loadUser = async () => {
      const data = await fetchUserFromRedis();
      if (data) setUser(data);
    };
    loadUser();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.name.trim().length < 3) {
      setErrors((prev) => ({ ...prev, name: "Name must be at least 3 characters long" }));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setDialogConfig({
        title: "Password Mismatch",
        message: "The passwords you entered do not match. Please try again.",
        type: "error",
        buttonText: "OK",
      });
      setDialogOpen(true);
      return;
    }

    try {
      const response = await axios.post(signupAPI, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        role: formData.role,
      });

      if (response.data.success) {
        const userRole = response.data.user.role?.toLowerCase().trim();
        const resetForm = () =>
          setFormData({ name: "", email: "", phone: "", password: "", confirmPassword: "", role: "admin" });

        if (userRole === "medicineadmin" || userRole === "diagnosticadmin" || userRole === "deliveryagent" || userRole==="staff") {
          setDialogConfig({
            title: "Registration Pending Approval",
            message: `Registration successful!\n\nYour account is pending admin approval. You will be notified via email once your account is approved.\n\nRole: ${
              userRole === "medicineadmin" ? "Medicine Admin" : "Diagnostic Admin"
            }`,
            type: "warning",
            buttonText: "Go to Login",
          });
          setDialogOpen(true);
          resetForm();
        } else {
          setDialogConfig({
            title: "Registration Successful",
            message: "Your account has been created successfully!\n\nRedirecting to dashboard...",
            type: "success",
            buttonText: "Continue",
          });
          setDialogOpen(true);
          if (response.data.token) sessionStorage.setItem("token", response.data.token);
          sessionStorage.setItem("userId", response.data.user.userId);
          resetForm();
          setTimeout(() => navigate("/admin-dashboard"), 2000);
        }
      }
    } catch (error) {
      setDialogConfig({
        title: "Registration Failed",
        message: error.response?.data?.error || "Something went wrong. Please try again.",
        type: "error",
        buttonText: "Try Again",
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    if (dialogConfig.title === "Registration Pending Approval") navigate("/login");
  };

  const fieldSize = isMobile ? "small" : "medium";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          p: { xs: 2, sm: 3, md: 4 },
          backgroundColor: "#fff",
        }}
      >
        <Card
          sx={{
            borderRadius: "16px",
            maxWidth: "1000px",
            width: "100%",
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
          }}
        >
          <Grid container>

            {/* ── Left Section (hidden on mobile) ── */}
            {!isMobile && (
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  background: "linear-gradient(135deg, #004f54, #007a85, #007a85)",
                  color: "white",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  p: isTablet ? 3 : 4,
                  width: isTablet ? "50%" : "50%",
                }}
              >
                {/* Logo */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box
                    component="img"
                    src={ayujalogo}
                    alt="Ayuja Logo"
                    sx={{ height: isTablet ? "60px" : "80px", width: "auto" }}
                  />
                </Box>

                <Typography
                  variant={isTablet ? "h5" : "h4"}
                  sx={{ fontWeight: "bold", mb: 2, textAlign: "center" }}
                >
                  Join <br /> Ayuja Today
                </Typography>

                <Typography
                  variant={isTablet ? "body1" : "h6"}
                  sx={{ mb: 2, textAlign: "center", fontSize: isTablet ? "14px" : undefined }}
                >
                  Transform lives through personalized in-home care. Become part of a
                  community that values compassion, dignity, and exceptional service.
                </Typography>

                {/* Feature badges */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: isTablet ? 1.5 : 3,
                    mb: 4,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2">✅ 24/7 Support</Typography>
                  <Typography variant="body2">✅ Certified Caregivers</Typography>
                </Box>

                {/* Emergency Support Box */}
                <Box
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    p: isTablet ? 2 : 3,
                    textAlign: "center",
                    width: isTablet ? "85%" : "60%",
                    mx: "auto",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    mb={1}
                    fontSize={isTablet ? "13px" : undefined}
                  >
                    Emergency Support Available
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, fontSize: isTablet ? "12px" : undefined }}>
                    Need immediate assistance? Our emergency support team is available 24/7
                    to help you and your loved ones.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#80ED99",
                      "&:hover": { backgroundColor: "#80ED99" },
                      color: "#22577A",
                      textTransform: "none",
                      borderRadius: "8px",
                      fontSize: isTablet ? "12px" : undefined,
                    }}
                  >
                    Contact Emergency
                  </Button>
                </Box>
              </Grid>
            )}

            {/* ── Right Section (Sign Up Form) ── */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                backgroundColor: "white",
                p: isMobile ? 3 : isTablet ? 3 : 4,
                display: "flex",
                flexDirection: "column",
                width: isMobile ? "100%" : "50%",
                // Allow scrolling on small screens if content overflows
                overflowY: isMobile ? "auto" : "visible",
                maxHeight: isMobile ? "100vh" : "none",
              }}
            >
              {/* Show logo on mobile (left panel is hidden) */}
              {isMobile && (
                <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                  <Box
                    component="img"
                    src={ayujalogo}
                    alt="Ayuja Logo"
                    sx={{ height: "56px", width: "auto" }}
                  />
                </Box>
              )}

              <form onSubmit={handleSubmit}>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  sx={{ fontWeight: "bold", mb: 1, textAlign: isMobile ? "center" : "left" }}
                >
                  Create Account
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "text.secondary", textAlign: isMobile ? "center" : "left" }}
                >
                  Get started with your personalized care dashboard
                </Typography>

                {/* Full Name */}
                <TextField
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  fullWidth
                  margin="normal"
                  size={fieldSize}
                  required
                  error={Boolean(errors.name)}
                  helperText={errors.name}
                />

                {/* Email */}
                <TextField
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value.toLowerCase() })
                  }
                  placeholder="Enter your email address"
                  fullWidth
                  margin="normal"
                  size={fieldSize}
                  required
                />

                {/* Mobile */}
                <TextField
                  label="Mobile Number"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                    setFormData({ ...formData, phone: value });
                  }}
                  placeholder="Enter 10-digit mobile number"
                  fullWidth
                  margin="normal"
                  size={fieldSize}
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+91</InputAdornment>,
                  }}
                />

                {/* Password */}
                <TextField
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  fullWidth
                  required
                  margin="normal"
                  size={fieldSize}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size={isMobile ? "small" : "medium"}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Confirm Password */}
                <TextField
                  label="Confirm Password"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your password"
                  fullWidth
                  required
                  margin="normal"
                  size={fieldSize}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size={isMobile ? "small" : "medium"}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Role Selection */}
                <FormControl fullWidth margin="normal" size={fieldSize} required>
                  <InputLabel>Select Role</InputLabel>
                  <Select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    label="Select Role"
                  >
                    {roleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Approval notice */}
                {(formData.role === "medicineadmin" || formData.role === "diagnosticadmin") && (
                  <Alert severity="info" sx={{ mt: 1, mb: 1, fontSize: isMobile ? "12px" : undefined }}>
                    Note: This role requires admin approval before you can access the system.
                  </Alert>
                )}

                <FormControlLabel
                  control={<Checkbox size={isMobile ? "small" : "medium"} />}
                  required
                  label={
                    <Typography variant="body2" fontSize={isMobile ? "12px" : undefined}>
                      Accept Ayuja's Terms and Conditions
                    </Typography>
                  }
                  sx={{ mt: 1 }}
                />

                {/* Register Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#007a85",
                    "&:hover": { backgroundColor: "#005f64" },
                    mt: 2,
                    mb: 3,
                    py: isMobile ? 1 : 1.5,
                    fontSize: isMobile ? "14px" : undefined,
                  }}
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : "Sign Up"}
                </Button>
              </form>

              <Typography
                sx={{ textAlign: "center", mb: 2, fontSize: isMobile ? "14px" : undefined }}
              >
                Already have an account?{" "}
                <a href="/Login" style={{ color: "#007a85", fontWeight: "bold" }}>
                  Sign In
                </a>
              </Typography>
            </Grid>
          </Grid>
        </Card>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Custom Dialog */}
        <CustomDialog
          open={dialogOpen}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type}
          buttonText={dialogConfig.buttonText}
          onClose={handleDialogClose}
        />
      </Box>
    </>
  );
};

export default Register;